import React, { useState } from "react";
import { Loader2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * Fix I (2026-09-04): pantalla que ve un usuario que entró con una clave temporal.
 * Reemplaza toda la app hasta que la cambia: no hay menú, no hay forma de saltearla.
 */
export default function ForcePasswordChange() {
  const { t } = useT();
  const { user, patchUser, logout } = useAuth();
  const [actual, setActual] = useState("");
  const [nueva, setNueva] = useState("");
  const [repetir, setRepetir] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (nueva !== repetir) {
      setError(t("password.mismatch"));
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/change-password", {
        password_actual: actual,
        password_nueva: nueva,
      });
      patchUser({ ...user, must_change_password: false });
      toast.success(t("password.changed"));
    } catch (err) {
      setError(apiError(err, t("app.error")));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-paper p-6">
      <form
        onSubmit={submit}
        data-testid="force-password-form"
        className="w-full max-w-[420px] rounded-[10px] border border-line bg-white p-6"
      >
        <div className="mb-4 flex items-center gap-2 text-ink">
          <ShieldAlert className="h-4 w-4" />
          <h1 className="text-[15px] font-semibold">{t("password.force_title")}</h1>
        </div>
        <p className="mb-5 text-[13px] leading-relaxed text-ink-tertiary">
          {t("password.force_desc")}
        </p>

        <div className="space-y-3">
          <div>
            <Label className="mb-1.5 block text-[12px] text-ink-tertiary">
              {t("password.current")}
            </Label>
            <Input
              type="password"
              autoComplete="current-password"
              data-testid="force-password-current"
              value={actual}
              onChange={(e) => setActual(e.target.value)}
              className="h-9 rounded-[6px] text-[13.5px]"
            />
          </div>
          <div>
            <Label className="mb-1.5 block text-[12px] text-ink-tertiary">
              {t("password.new")}
            </Label>
            <Input
              type="password"
              autoComplete="new-password"
              data-testid="force-password-new"
              value={nueva}
              onChange={(e) => setNueva(e.target.value)}
              className="h-9 rounded-[6px] text-[13.5px]"
            />
          </div>
          <div>
            <Label className="mb-1.5 block text-[12px] text-ink-tertiary">
              {t("password.repeat")}
            </Label>
            <Input
              type="password"
              autoComplete="new-password"
              data-testid="force-password-repeat"
              value={repetir}
              onChange={(e) => setRepetir(e.target.value)}
              className="h-9 rounded-[6px] text-[13.5px]"
            />
          </div>
        </div>

        <p className="mt-3 text-[12px] text-ink-tertiary">{t("password.rules")}</p>
        {error && (
          <p data-testid="force-password-error" className="mt-3 text-[12.5px] text-red-600">
            {error}
          </p>
        )}

        <Button
          type="submit"
          disabled={loading}
          data-testid="force-password-submit"
          className="mt-5 h-9 w-full rounded-[6px] bg-brand text-[13px] hover:bg-brand-hover"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("password.save")}
        </Button>
        <button
          type="button"
          onClick={logout}
          className="mt-3 w-full text-[12.5px] text-ink-tertiary hover:underline"
        >
          {t("app.logout")}
        </button>
      </form>
    </div>
  );
}
